import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@radix-ui/themes';
import {
  CHOOSE,
  MESSAGE,
  REVIEW_WITHOUT_NOTIFICATION,
} from '../../utils/constants';
import Text from '../../../../shared/Text';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

const AdvancedChooseStep = ({ setStep }) => {
  const { t } = useTranslation('postNotifications');
  const { campaign } = useCampaignContext();
  const isNewHireCampaign = campaign?.newHire;

  return (
    <StepWrapper>
      <Wrapper>
        {isNewHireCampaign ? (
          <Tooltip content={t('chooseStep.notAvailableForNewHire')} side="top">
            <ChooseOption
              $isLocked={true}
              onClick={() =>
                !isNewHireCampaign &&
                setStep(prevState => ({
                  ...prevState,
                  currentStep: MESSAGE,
                  previousStep: CHOOSE,
                }))
              }
            >
              <IconWrapper>
                <i className="fa-regular fa-bell fa-2xl"></i>
              </IconWrapper>
              <TileWrapper>
                <Text variant="h5">{t('chooseStep.publish')}</Text>{' '}
                <Text variant="h5" color="success.default">
                  {t('chooseStep.with')}
                </Text>{' '}
                <Text variant="h5">{t('chooseStep.notifications')}</Text>
              </TileWrapper>
              <Text variant="body3">
                {t('chooseStep.withNotificationsDescription')}
              </Text>
            </ChooseOption>
          </Tooltip>
        ) : (
          <ChooseOption
            onClick={() =>
              setStep(prevState => ({
                ...prevState,
                currentStep: MESSAGE,
                previousStep: CHOOSE,
              }))
            }
          >
            <IconWrapper>
              <i className="fa-regular fa-bell fa-2xl"></i>
            </IconWrapper>
            <TileWrapper>
              <Text variant="h5">{t('chooseStep.publish')}</Text>{' '}
              <Text variant="h5" color="success.default">
                {t('chooseStep.with')}
              </Text>{' '}
              <Text variant="h5">{t('chooseStep.notifications')}</Text>
            </TileWrapper>
            <Text variant="body3">
              {t('chooseStep.withNotificationsDescription')}
            </Text>
          </ChooseOption>
        )}
        <ChooseOption
          onClick={() =>
            setStep(prevState => ({
              ...prevState,
              currentStep: REVIEW_WITHOUT_NOTIFICATION,
              previousStep: CHOOSE,
            }))
          }
        >
          <IconWrapper>
            <i className="fa-regular fa-bolt fa-2xl"></i>
          </IconWrapper>
          <TileWrapper>
            <Text variant="h5">{t('chooseStep.publish')}</Text>{' '}
            <Text variant="h5" color="error.default">
              {t('chooseStep.without')}
            </Text>{' '}
            <Text variant="h5">{t('chooseStep.notifications')}</Text>
          </TileWrapper>
          <Text variant="body3">
            {t('chooseStep.withoutNotificationsDescription')}
          </Text>
        </ChooseOption>
      </Wrapper>
      <Text margin="0 auto 0 0" variant="h6">
        {t('chooseStep.help')}
      </Text>
      <Iframe
        src="https://fast.wistia.net/embed/iframe/yvqnu1rp0b"
        title="Notifications Explainer Video"
        allow="autoplay; fullscreen"
        allowtransparency="true"
        frameBorder="0"
        scrolling="no"
        className="wistia_embed"
        name="wistia_embed"
        msallowfullscreen
        width="100%"
        height="445"
      />
      <Text margin="0 auto 0 0" variant="body2">
        {t('chooseStep.videoDescription')}
      </Text>
    </StepWrapper>
  );
};

export const ChooseStep = ({ setStep }) => {
  return <AdvancedChooseStep setStep={setStep} />;
};

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 832px;
  gap: 24px;
  margin: 0 auto;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${themeGet('colors.black.10')};
  background: ${themeGet('colors.white')};
`;

const Wrapper = styled.div`
  display: flex;
  margin: 0 auto;
  height: 262px;
  gap: 24px;
`;

const TileWrapper = styled.div`
  text-wrap: nowrap;
`;

const IconWrapper = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
`;

const ChooseOption = styled.div`
  padding: 40px;
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px;
  width: 379px;
  cursor: ${props => (props.$isLocked ? 'not-allowed' : 'pointer')};
  gap: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${props => (props.$isLocked ? 0.5 : 1)};

  &:hover {
    box-shadow: 0px 8px 20px 0px rgba(37, 40, 43, 0.08);
  }

  &:active {
    border: 1px solid ${themeGet('colors.primary.100')};
    background: ${themeGet('colors.primary.20')};
  }
`;

const Iframe = styled.iframe`
  border-radius: 8px;
`;
